export const billingConfiguration = {
  header: {
    title: "Configurazione della fatturazione",
    description:
      "Gestisci gli emittenti di fatturazione del tuo account e collega ogni alloggio a quello corretto.",
  },

  intro: {
    lead:
      "Un <strong>emittente di fatturazione</strong> è l'entità fiscale che fattura ai tuoi ospiti. Puoi averne diversi e scegliere quale fattura ogni alloggio.",
    feats: {
      fiscalData: {
        label: "Dati fiscali propri",
        tip: "Denominazione legale, NIF/CIF e indirizzo fiscale mostrati sulla fattura.",
      },
      series: {
        label: "Serie indipendente",
        tip: "Ogni emittente mantiene la propria serie e sequenza (e quelle per le fatture rettificative), senza mai mescolarle.",
      },
      verifactu: {
        label: "VeriFactu per emittente",
        tip: "Ogni emittente soddisfa VeriFactu (firma e registrazione presso l'AEAT) separatamente, in base alla propria configurazione fiscale.",
      },
      assignment: {
        label: "Assegnazione per alloggio",
        tip: "Collega ogni alloggio a un emittente. Quelli non assegnati usano l'emittente principale.",
      },
    },
    foot: "Al momento dell'emissione, viene usato l'emittente dell'alloggio della prenotazione (al check-out o quando l'ospite lo richiede all'agente IA).",
  },

  issuers: {
    card: {
      title: "Emittenti di fatturazione",
      subtitle: "Crea più emittenti e assegna i tuoi alloggi a ciascuno.",
    },
    searchPlaceholder: "Cerca emittente per nome o NIF…",
    newButton: "Nuovo emittente",
    noName: "Senza nome",
    defaultBadge: "Principale",
    listingsCount: "{count} alloggi",
    empty: {
      noMatch: "Nessun emittente corrisponde alla tua ricerca.",
      none: "Non hai ancora nessun emittente. Crea il primo con “Nuovo emittente”.",
    },
  },

  assign: {
    card: {
      title: "Assegnazione degli alloggi",
      subtitle:
        "Scegli quale emittente fattura ogni alloggio. Un alloggio può avere un solo emittente; quelli non assegnati usano il principale.",
    },
    searchPlaceholder: "Cerca alloggio…",
    filter: {
      all: "Tutti",
      unassigned: "Non assegnati",
    },
    issuerFallback: "Emittente #{id}",
    principalSuffix: " (principale)",
    unassignedOption: "— Non assegnato (principale)",
    empty: {
      noMatch: "Nessun alloggio corrisponde al filtro.",
      none: "Non hai ancora nessun alloggio.",
    },
    count: "{shown} di {total} alloggi",
  },

  drawer: {
    editTitle: "Modifica emittente",
    newTitle: "Nuovo emittente",
    subtitle: "Dati fiscali dell'emittente di fatturazione",
    closeAria: "Chiudi",
    footer: {
      isDefault: "È il principale",
      setDefault: "Imposta come principale",
      delete: "Elimina",
      cancel: "Annulla",
      saving: "Salvataggio...",
      save: "Salva emittente",
    },
  },

  company: {
    title: "Dati dell'azienda",
    subtitle: "Informazioni fiscali della tua struttura",
    country: {
      label: "Paese di fatturazione",
      hint: "Scegli il paese fiscale applicabile a questa configurazione.",
    },
    personType: {
      label: "Tipo di entità",
      company: "Persona giuridica (società)",
      individual: "Persona fisica (lavoratore autonomo)",
    },
    legalName: {
      label: "Nome / Ragione sociale",
      placeholderCompany: "es. INNDEV S.L.",
      placeholderIndividual: "es. María García",
      hint: "Denominazione fiscale della società o della persona fisica",
    },
    co: {
      regime: {
        label: "Regime DIAN",
        common: "Regime ordinario",
        simplified: "Regime semplificato",
        hint: "Obbligatorio per la fatturazione elettronica DIAN",
      },
      municipality: {
        label: "Comune DIAN",
        hint: "Codice DIVIPOLA del tuo comune — richiesto dalla DIAN.",
        selected: "Selezionato: {code}",
      },
      responsibilities: {
        label: "Responsabilità fiscali",
        placeholder: "es. O-13, R-99-PN",
        hint: "Codici separati da virgola. Il più comune: <strong>O-13</strong>",
      },
    },
    mx: {
      regime: {
        label: "Regime fiscale (SAT)",
        placeholder: "es. 601",
        hint: "Codice del regime fiscale SAT",
      },
      zip: {
        label: "CAP fiscale (SAT)",
        placeholder: "es. 06600",
        hint: "Codice postale fiscale SAT",
      },
    },
  },

  address: {
    title: "Indirizzo fiscale",
    subtitle: "L'indirizzo mostrato sulle fatture",
    street: {
      label: "Via e numero",
    },
    city: {
      label: "Città",
      hint: "Città e codice postale",
    },
    email: {
      label: "Email di fatturazione",
      placeholder: "billing@yourproperty.com",
    },
    phone: {
      label: "Telefono",
    },
    ph: {
      ES: {
        street: "es. Gran Vía 42, piano 3",
        city: "Madrid",
        zip: "28001",
        regionLabel: "Provincia",
        region: "Madrid",
        phone: "+34 123 456 789",
      },
      CO: {
        street: "es. Calle 13 No. 10-25",
        city: "Bogotá",
        zip: "110111",
        regionLabel: "Dipartimento",
        region: "Cundinamarca",
        phone: "+57 601 234 5678",
      },
      MX: {
        street: "es. Av. Insurgentes Sur 1234",
        city: "Città del Messico",
        zip: "06600",
        regionLabel: "Stato",
        region: "CDMX",
        phone: "+52 55 1234 5678",
      },
    },
  },

  bank: {
    title: "Conto per gli incassi",
    subtitle: "Dove ti pagano se incassi con bonifico",
    iban: {
      label: "Numero di conto (IBAN)",
      placeholder: "IT60 X054 2811 1010 0000 0123 456",
      hint: "Compare sulla fattura solo quando la modalità di pagamento è il bonifico.",
      error: "Il numero di conto (IBAN) non è valido.",
    },
    bic: {
      label: "BIC / SWIFT",
      placeholder: "UNCRITMM",
      hint: "Facoltativo. Serve per gli incassi da fuori l'area SEPA.",
    },
    holder: {
      label: "Intestatario del conto",
      placeholder: "Lascia vuoto se coincide con la ragione sociale",
      hint: "Solo se il conto è intestato a un nome diverso da quello dell'azienda.",
    },
  },

  invoice: {
    title: "Configurazione della fattura",
    subtitle: "Formato, valuta e numerazione dei tuoi documenti",
    currency: {
      label: "Valuta predefinita",
      hint: "Valuta con cui verranno emesse le fatture",
    },
    taxRate: {
      label: "IVA % predefinita",
      hintCO: "IVA generale Colombia: <strong>19%</strong>",
      hintMX: "IVA generale Messico: <strong>16%</strong>",
      hintES: "IVA ridotta per il settore alberghiero in Spagna: <strong>10%</strong> — generale: <strong>21%</strong>",
    },
    type: {
      label: "Tipo di fattura",
      detailed: {
        label: "Dettagliata",
        desc: "Ogni servizio sulla propria riga",
      },
      simplified: {
        label: "Semplificata",
        desc: "Ricevuta o fattura con voci raggruppate",
      },
    },
    numbering: {
      label: "Numerazione dei documenti",
      example: "es. {value}",
      invoices: {
        title: "Fatture",
      },
      series: "Serie",
      optional: "(opzionale)",
      optionalPlaceholder: "Opzionale",
      digits: "Cifre",
      nextNumber: "Numero successivo",
      hintOptional: "La serie è opzionale. Se lasciata vuota, la fattura verrà emessa con il solo numero sequenziale.",
      hintFormat: "Se imposti una serie, il sistema costruirà il codice come SERIE-NUMERO. Senza serie, produrrà solo NUMERO.",
      hintWarn: "Imposta questo valore prima di emettere la prima fattura della serie per mantenere allineata la sequenza fiscale.",
      hintSandboxCO: "In modalità sandbox usa <strong>SETP</strong> — in produzione deve corrispondere alla tua risoluzione DIAN.",
    },
    creditNotes: {
      title: "Note di credito",
      badge: "Annullamenti / rettifiche",
      hint: "Usato per la prossima fattura rettificativa o annullamento che emetti.",
    },
    footer: {
      label: "Piè di pagina predefinito",
      hint: "Testo che apparirà in fondo a tutte le fatture",
      phES: "es. Operazione soggetta all'IVA ridotta per il settore alberghiero ai sensi dell'art. 91 della Legge IVA",
      phCO: "es. Risoluzione DIAN n. 000123 del 01-01-2025. Intervalli da 1 a 5000000",
      phMX: "es. CFDI emesso secondo i requisiti dell'articolo 29-A del CFF",
    },
  },

  advanced: {
    title: "Opzioni avanzate",
    subtitle: "Impostazioni di consegna aggiuntive",
    attachPdf: {
      label: "Allega PDF",
      text: "Includi il documento PDF insieme al link per il download",
    },
  },

  loading: {
    default: "Caricamento...",
    config: "Caricamento della configurazione...",
    profiles: "Caricamento degli emittenti...",
    saving: "Salvataggio della configurazione...",
  },

  countries: {
    ES: "Spagna",
    CO: "Colombia",
    MX: "Messico",
  },

  currencies: {
    EUR: "Euro",
    COP: "Peso colombiano",
    USD: "Dollaro statunitense",
    MXN: "Peso messicano",
  },

  taxId: {
    labelFallback: "Codice fiscale",
    helpES: "NIF per persone fisiche, CIF per società. es. B12345678",
    helpCO: "NIT con cifra di controllo. es. 9014514812 (10 cifre)",
    helpMX: "RFC di 12 o 13 caratteri. es. XAXX010101000",
    helpFallback: "Identificativo fiscale",
  },

  validation: {
    taxIdES: "Formato non valido. es. B12345678",
    taxIdCO: "Il NIT deve avere 9 o 10 cifre. es. 9014514812",
    taxIdMX: "Formato RFC non valido. es. XAXX010101000",
  },

  msg: {
    reassignError: "Impossibile riassegnare l'alloggio.",
    defaultUpdated: "Emittente principale aggiornato.",
    setDefaultError: "Impossibile impostare come principale.",
    deleteConfirm: "Eliminare questo emittente di fatturazione?",
    deleted: "Emittente eliminato.",
    deleteError: "Impossibile eliminare l'emittente.",
    requiredFields: "Compila tutti i campi obbligatori prima di salvare.",
    fixTaxId: "Correggi l'identificativo fiscale prima di salvare.",
    coRegimeRequired: "Il regime DIAN è obbligatorio per la Colombia.",
    coMunicipalityRequired: "Il comune DIAN è obbligatorio per la Colombia.",
    savedOk: "Emittente salvato con successo.",
    saveError: "Errore durante il salvataggio della configurazione.",
  },

  misc: {
    thisIssuer: "questo emittente",
  },
};
